import {
  singularOrPlural,
  truncate,
  uppercaseFirstLetter,
} from '@/Common/util/string'
import {
  isToolPart,
  reasoningDisplayFromText,
  toolNameFromPart,
} from '../BidBoostLangGraphTransport'

export function reasoningActivityLabel(part, turnStatus) {
  return (
    part.title?.trim() ||
    reasoningDisplayFromText(part.text).title ||
    (turnStatus === 'running' && part.state === 'streaming'
      ? 'Thinking…'
      : 'Thought')
  )
}

export function latestActivityLabel(parts, turnStatus) {
  for (let index = parts.length - 1; index >= 0; index--) {
    const part = parts[index]
    if (isToolPart(part)) {
      if (
        toolActivityState(part, turnStatus) === 'running' &&
        part.children?.length
      ) {
        const childLabel = latestActivityLabel(part.children, turnStatus)
        if (childLabel) return childLabel
      }
      return toolActivityLabel(part, turnStatus)
    }
    if (part.type === 'reasoning') {
      const reasoning = reasoningDisplayFromText(part.text)
      if (part.title?.trim() || reasoning.title || reasoning.text?.trim())
        return reasoningActivityLabel(part, turnStatus)
    }
  }
  return null
}

const actions = {
  search: ['Searching', 'Searched', 'search'],
  find: ['Finding', 'Found', 'find'],
  get: ['Reading', 'Read', 'read'],
  read: ['Reading', 'Read', 'read'],
  list: ['Listing', 'Listed', 'list'],
  create: ['Creating', 'Created', 'create'],
  add: ['Adding', 'Added', 'add'],
  update: ['Updating', 'Updated', 'update'],
  patch: ['Updating', 'Updated', 'update'],
  edit: ['Editing', 'Edited', 'edit'],
  delete: ['Deleting', 'Deleted', 'delete'],
  remove: ['Removing', 'Removed', 'remove'],
  check: ['Checking', 'Checked', 'check'],
  validate: ['Checking', 'Checked', 'check'],
  prepare: ['Preparing', 'Prepared', 'prepare'],
  start: ['Starting', 'Started', 'start'],
  confirm: ['Confirming', 'Confirmed', 'confirm'],
  cancel: ['Canceling', 'Canceled', 'cancel'],
  save: ['Saving', 'Saved', 'save'],
  assign: ['Assigning', 'Assigned', 'assign'],
  move: ['Moving', 'Moved', 'move'],
  reschedule: ['Rescheduling', 'Rescheduled', 'reschedule'],
  place: ['Placing', 'Placed', 'place'],
  return: ['Returning', 'Returned', 'return'],
  export: ['Exporting', 'Exported', 'export'],
  upload: ['Uploading', 'Uploaded', 'upload'],
  send: ['Sending', 'Sent', 'send'],
  execute: ['Running', 'Ran', 'run'],
  run: ['Running', 'Ran', 'run'],
}

const internalActions = {
  save_user_memory: ['Updating memory', 'Memory updated', 'update memory'],
  ls: ['Listing files', 'Listed files', 'list files'],
  glob: ['Finding files', 'Found files', 'find files'],
  grep: ['Searching files', 'Searched files', 'search files'],
  read_file: ['Reading a file', 'Read a file', 'read a file'],
  write_file: ['Writing a file', 'Wrote a file', 'write a file'],
  edit_file: ['Editing a file', 'Edited a file', 'edit a file'],
  write_todos: ['Updating the plan', 'Updated the plan', 'update the plan'],
  ask_user_text: [
    'Asking a question',
    'Received your answer',
    'ask a question',
  ],
  ask_user_choice: [
    'Asking a question',
    'Received your answer',
    'ask a question',
  ],
  ask_user_form: [
    'Asking a few questions',
    'Received your answers',
    'ask a few questions',
  ],
  notify_page_updated: [
    'Flagging this page for reload',
    'Flagged this page for reload',
    'flag this page for reload',
  ],
}

export function toolActivityState(part, turnStatus) {
  if (part.state === 'output-error' || part.errorText || part.output?.isError) {
    return 'error'
  }
  if (part.state === 'output-denied') return 'denied'
  if (part.state === 'output-available' || part.output !== undefined)
    return 'complete'
  if (['approval-requested', 'response-requested'].includes(part.state))
    return 'waiting'
  if (turnStatus === 'running') return 'running'
  if (turnStatus === 'waiting') return 'waiting'
  if (turnStatus === 'stopped') return 'stopped'
  return 'unresolved'
}

function actionLabels(part) {
  const name = toolNameFromPart(part)
  if (internalActions[name]) return internalActions[name]

  if (name === 'task') {
    const specialist = String(
      part.input?.subagent_type || 'a specialist'
    ).replaceAll('-', ' ')
    return [
      `Working with ${specialist}`,
      `Worked with ${specialist}`,
      `work with ${specialist}`,
    ]
  }

  const words = name.split(/[_-]+/)
  const actionIndex = words.findIndex((word) => actions[word])
  if (actionIndex !== -1) {
    const noun =
      words.slice(actionIndex + 1).join(' ') ||
      words.slice(0, actionIndex).join(' ') ||
      'tool'
    return actions[words[actionIndex]].map((verb) => `${verb} ${noun}`)
  }

  const title = words.join(' ')
  return [`Running ${title}`, `Ran ${title}`, `run ${title}`]
}

export function toolActivityLabel(part, turnStatus = 'complete') {
  const [running, complete, infinitive] = actionLabels(part)
  switch (toolActivityState(part, turnStatus)) {
    case 'running':
      return `${running}…`
    case 'complete':
      return complete
    case 'error':
      return `Failed to ${infinitive}`
    case 'denied':
      return `Skipped: ${infinitive}`
    case 'waiting':
      return `Waiting to ${infinitive}`
    case 'stopped':
      return `Stopped: ${infinitive}`
    default:
      return `No result: ${infinitive}`
  }
}

export function toolActivityTarget(part) {
  const input = part.input
  if (!input || typeof input !== 'object') return ''
  const target = [
    input.command,
    input.query,
    input.search,
    input.search_term,
    input.file_path,
    input.path,
    input.name,
  ].find((value) => typeof value === 'string' && value.trim())
  return target ? truncate(target.replace(/\s+/g, ' ').trim(), 80, false) : ''
}

export function toolGroupLabel(parts, turnStatus) {
  const active = parts.filter(
    (part) => toolActivityState(part, turnStatus) === 'running'
  )
  const labels = [
    ...new Set(
      (active.length ? active : parts).map((part) =>
        toolActivityLabel(part, turnStatus).replace(/…$/, '')
      )
    ),
  ]
  const shown = labels
    .slice(0, 2)
    .map((label, index) =>
      index ? label.charAt(0).toLowerCase() + label.slice(1) : label
    )
  let label = shown.join(' and ')
  if (labels.length > 2) label += ` + ${labels.length - 2} more`
  if (parts.length > 1)
    label += ` · ${parts.length} ${singularOrPlural(parts.length, 'action', 'actions')}`
  return `${uppercaseFirstLetter(label)}${active.length ? '…' : ''}`
}
